import styled from '@emotion/styled';
import * as tokens from '@tokens';

export interface ProgressBarProps {
  /**
   * Styles override.
   */
  className?: string;

  /**
   * The height of the progress bar. This translates in s=4px and m=8px
   */
  size?: 's' | 'm';

  /**
   * The current value of the progress.
   * This represents a percentage and it should be between 0 and 100.
   */
  currentValue: number;
}

const SIZES = {
  s: 4,
  m: 8,
};
const MIN_VALUE = 0;
const MAX_VALUE = 100;

interface ContainerProps {
  height: number;
}

interface ProgressProps {
  currentPercentage: number;
}

const Container = styled.div<ContainerProps>`
  height: ${({ height }) => height}px;
  overflow: hidden;
  border-radius: ${tokens.radius.l};
  background-color: ${tokens.color.neutral_200};
`;

const Progress = styled.div<ProgressProps>`
  height: 100%;
  background-color: ${tokens.color.ui_active};
  width: ${({ currentPercentage }) => currentPercentage}%;
  ${tokens.transition('width', 350)};
`;

/**
 * A way of showing the progress of a task.
 *
 * {@link https://zeroheight.com/76c439891/p/403e06-progress-bar Usage guidelines}
 *
 * {@link https://storybook.hotjar.com/?path=/docs/components-progressbar--basic-progress-bar Code documentation}
 *
 * @example
 * <ProgressBar currentValue={50} />
 */
export const ProgressBar = ({
  className,
  currentValue,
  size = 'm',
}: ProgressBarProps): JSX.Element => {
  return (
    <Container
      className={className}
      height={SIZES[size]}
      role="progressbar"
      aria-valuemin={MIN_VALUE}
      aria-valuemax={MAX_VALUE}
      aria-valuenow={currentValue}
    >
      <Progress currentPercentage={currentValue} />
    </Container>
  );
};

ProgressBar.displayName = 'ProgressBar';
